import { Box, Grid, GridItem } from "@chakra-ui/react";
import React, { useRef } from "react";

import { useIsSmallScreen } from "../../../hooks/useIsSmallScreen";
import useWindowDimensions from "../../../hooks/useWindowDimensions";

type AiRecordingLayoutProps = {
  header: React.ReactNode;
  player: React.ReactNode;
  main: React.ReactNode;
  sidebar: React.ReactNode;
  /**
   * `children` is used for content like modals that needs to exist
   * in the DOM but does not need its root node to occupy space or be visible
   */
  children?: React.ReactNode;
};

const AiRecordingLayout: React.FC<AiRecordingLayoutProps> = ({
  header,
  player,
  main,
  sidebar,
  children,
}) => {
  const { layoutHeight } = useWindowDimensions();
  const isSmallScreen = useIsSmallScreen();

  const playerRef = useRef<HTMLDivElement | null>(null);

  const headerRef = useRef<HTMLDivElement | null>(null);

  return (
    <Box height={layoutHeight} overflowY={isSmallScreen ? "hidden" : "auto"}>
      <Grid
        height="100%"
        minH="100%"
        background="white"
        borderColor="gray.100"
        templateColumns={{
          base: "1fr",
          lg: "minmax(200px, 60%) minmax(460px, 1fr)",
        }}
        templateRows="repeat(2, min-content) 1fr 0"
        templateAreas={{
          base: `
            "player"
            "header"
            "main"
            "hidden"`,
          lg: `
            "header      header"
            "main        player"
            "main        sidebar"
            "hidden      hidden"`,
        }}
      >
        <GridItem
          area="header"
          borderBottomWidth={{ base: "0", lg: "1px" }}
          ref={headerRef}
        >
          {header}
        </GridItem>

        <GridItem area="player" ref={playerRef}>
          {player}
        </GridItem>

        <GridItem
          bg="white"
          gridArea="main"
          overflowY={{ base: "hidden", lg: "auto" }}
          borderRightWidth="1px"
          minH="0"
          display="flex"
          flexDir="column"
        >
          {main}
        </GridItem>

        {!isSmallScreen && (
          <GridItem
            area="sidebar"
            bg="white"
            overflowY="auto"
            minH="0"
            display="flex"
            flexDir="column"
          >
            {sidebar}
          </GridItem>
        )}

        <GridItem area="hidden" overflow="hidden" height="0">
          {children}
        </GridItem>
      </Grid>
    </Box>
  );
};

export default AiRecordingLayout;
